import React, { useEffect, useMemo, useState } from "react";
import {
  Table,
  Button,
  Dropdown,
  Menu,
  Switch,
  Tooltip,
  Modal,
  Form,
  Input,
  Row,
  Col,
} from "antd";
import {
  MoreOutlined,
  ReloadOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import classNames from "classnames";
import { useSharedState } from "../../../Shared/Services/WindowSizeService";
import { ServiceUtils } from "../../../Shared/Utils/ServiceUtils";
import { alertService } from "../../../Shared/Utils/ToasterUtils";
import CryptoJS from "crypto-js";
import { getDecryptSession } from "../../../Shared/Utils/CommonUtils";

const UserManagement = () => {
  const navigate = useNavigate();
  const windowSize$ = useSharedState();
  const [form] = Form.useForm();

  const [userDetails, setUserDetails] = useState({});
  const [searchText, setSearchText] = useState("");

  const [rawHeaders, setRawHeaders] = useState([]);
  const [users, setUsers] = useState([]);

  // Password
  const [enablePasswordModal, setEnablePasswordModal] = useState(false);
  const [openReset, setOpenReset] = useState(false);
  const [currentUserId, setCurrentUserId] = useState(null);

  // SSO
  const [enableSSOLogin, setEnableSSOLogin] = useState(false);
  const [pendingSSO, setPendingSSO] = useState(null);
  const [ssoState, setSsoState] = useState({});

  // ---------------- FILTERED DATA ----------------
  const filteredData = useMemo(() => {
    if (!searchText) return users;
    const reg = new RegExp(searchText, "gi");
    return users.filter((r) =>
      [r.firstName, r.lastName, r.email, r.role].some((f) => f?.match(reg))
    );
  }, [searchText, users]);

  // ---------------- MENU ----------------
  const menu = (record) => (
    <Menu>
      <Menu.Item onClick={() => navigate(`/admin/user/${record.id}`)}>
        Edit
      </Menu.Item>
    </Menu>
  );

  // ---------------- COLUMNS (FIXED) ----------------
  const columns = useMemo(() => {
    return rawHeaders.map((el) => {
      // PASSWORD SWITCH
      if (el.key === "passwordEnabled") {
        return {
          ...el,
          render: (_, record) => (
            <div style={{ textAlign: "center" }}>
              <Switch
                checked={record.passwordEnabled}
                onChange={(val) =>
                  handlePasswordToggle(val, record.id)
                }
              />
              {record.passwordEnabled && (
                <Tooltip title="Reset password">
                  <ReloadOutlined
                    style={{ marginLeft: 15 }}
                    className="pointer"
                    onClick={() => openPasswordModal(record)}
                  />
                </Tooltip>
              )}
            </div>
          ),
        };
      }

      // SSO SWITCH (FIXED)
      if (el.key === "ssoEnabled") {
        return {
          ...el,
          render: (_, record) => (
            <Switch
              checked={ssoState[record.id] ?? record.ssoEnabled}
              onChange={(val) => {
                setPendingSSO({
                  userId: record.id,
                  value: val,
                });
                setEnableSSOLogin(true);
              }}
            />
          ),
        };
      }

      // ACTION
      if (el.key === "action") {
        return {
          ...el,
          render: (_, record) => (
            <Dropdown overlay={menu(record)} trigger={["click"]}>
              <Button type="link" icon={<MoreOutlined />} />
            </Dropdown>
          ),
        };
      }

      return el;
    });
  }, [rawHeaders, ssoState]);

  // ---------------- FETCH USERS ----------------
  const getUserTableDetails = () => {
    ServiceUtils.getRequest("usersList").then(
      (res) => {
        if (res.status === 200 && res.data.status) {
          const data = res.data.data;

          const ssoObj = {};
          data.bodyContent.forEach((u) => {
            ssoObj[u.id] = u.ssoEnabled;
          });

          setSsoState(ssoObj);
          setRawHeaders(data.headerContent);
          setUsers(data.bodyContent);
        }
      },
      () => alertService.error("Error fetching users")
    );
  };

  useEffect(() => {
    const session = localStorage.getItem("arcutis-user");
    const user = getDecryptSession(session);
    setUserDetails(user);
    if (user?.admin_access) getUserTableDetails();
  }, []);

  // ---------------- PASSWORD TOGGLE ----------------
  const handlePasswordToggle = (value, userId) => {
    if (!value) {
      ServiceUtils.postRequest("userOps", {
        userData: { passwordEnabled: false },
        type: "edit",
        userId,
      }).then(getUserTableDetails);
      return;
    }

    setCurrentUserId(userId);
    setEnablePasswordModal(true);
  };

  const openPasswordModal = (record) => {
    setCurrentUserId(record.id);
    setOpenReset(true);
  };

  const submitPassword = () => {
    const values = form.getFieldsValue();

    const encrypt = (text) =>
      CryptoJS.AES.encrypt(
        text,
        CryptoJS.enc.Utf8.parse("ThisIsASecureKey1234567890123456"),
        {
          iv: CryptoJS.enc.Utf8.parse("ThisIsAnIV123456"),
          mode: CryptoJS.mode.CBC,
          padding: CryptoJS.pad.Pkcs7,
        }
      ).toString();

    ServiceUtils.postRequest("userOps", {
      userData: {
        password: encrypt(values.password),
        oldPassword: values.oldPassword && encrypt(values.oldPassword),
        passwordEnabled: true,
      },
      type: "edit",
      userId: currentUserId,
    }).then(() => {
      setOpenReset(false);
      setEnablePasswordModal(false);
      form.resetFields();
      getUserTableDetails();
    });
  };

  // ---------------- SSO CONFIRM ----------------
  const confirmSSOLogin = () => {
    ServiceUtils.postRequest("userOps", {
      userData: { ssoEnabled: pendingSSO.value },
      type: "edit",
      userId: pendingSSO.userId,
    }).then(() => {
      setSsoState((prev) => ({
        ...prev,
        [pendingSSO.userId]: pendingSSO.value,
      }));
      setEnableSSOLogin(false);
      setPendingSSO(null);
    });
  };

  const cancelSSOLogin = () => {
    setEnableSSOLogin(false);
    setPendingSSO(null);
  };

  // ---------------- RENDER ----------------
  return (
    <div className="user-management">
      <Row style={{ marginBottom: 10 }}>
        <Col xs={24} lg={12}>
          <Input.Search
            value={searchText}
            size="large"
            placeholder="Search"
            onChange={(e) => setSearchText(e.target.value)}
          />
        </Col>
        <Col xs={24} lg={12} style={{ textAlign: "right" }}>
          {userDetails.admin_access && (
            <Button type="primary" onClick={() => navigate("/admin/user/add")}>
              Add User
            </Button>
          )}
        </Col>
      </Row>

      <div
        className={classNames({
          "survey-container": ["lg", "xl", "xxl"].includes(windowSize$),
          "survey-container-small": ["xs", "sm", "md"].includes(windowSize$),
        })}
      >
        <Table
          rowKey="id"
          columns={columns}
          dataSource={filteredData}
          pagination={false}
          bordered
        />
      </div>

      {/* RESET PASSWORD */}
      <Modal
        title="Reset password"
        open={openReset}
        onCancel={() => setOpenReset(false)}
        onOk={submitPassword}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="oldPassword"
            label="Old Password"
            tooltip={{
              title: "Not required for first reset",
              icon: <InfoCircleOutlined />,
            }}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="password"
            label="Password"
            rules={[{ required: true }]}
          >
            <Input.Password />
          </Form.Item>
          <Form.Item
            name="confirm"
            label="Confirm Password"
            dependencies={["password"]}
            rules={[
              { required: true },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue("password") === value)
                    return Promise.resolve();
                  return Promise.reject("Passwords do not match");
                },
              }),
            ]}
          >
            <Input.Password />
          </Form.Item>
        </Form>
      </Modal>

      {/* ENABLE PASSWORD */}
      <Modal
        open={enablePasswordModal}
        onOk={() => setOpenReset(true)}
        onCancel={() => setEnablePasswordModal(false)}
      >
        Please add a password to enable password login.
      </Modal>

      {/* SSO CONFIRM */}
      <Modal
        open={enableSSOLogin}
        onOk={confirmSSOLogin}
        onCancel={cancelSSOLogin}
      >
        Are you sure you want to{" "}
        <b>{pendingSSO?.value ? "enable" : "disable"}</b> SSO login?
      </Modal>
    </div>
  );
};

export default UserManagement;